import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Vibe } from "@majourneys/shared";
import {
  createInitialTripState,
  tripReducer,
  type TripAction,
  type TripState,
} from "./trip-reducer";
import {
  DEFAULT_TRIP_PEOPLE,
  MAX_TRIP_PEOPLE,
  migrateTrip,
  readTrips,
  writeTrips,
  readActiveTripId,
  writeActiveTripId,
  TRIPS_CHANGED_EVENT,
  TRIPS_STORAGE_KEY,
  type TripsChangedDetail,
} from "./trip-storage";
import { DiscoverContext } from "./discover-context";
import { generateId } from "../lib/id";
import { cleanRoute, daysFromRoute, type RouteLeg } from "../lib/trip-route";
import { useIsomorphicLayoutEffect } from "../lib/use-isomorphic-layout-effect";
import type {
  TripStayRef,
  DiscoverItem,
  Trip,
  TripDay,
  TripStop,
  TripStopSnapshot,
  TripStopSource,
  TripStyle,
} from "../types";

interface TripContextValue {
  state: TripState;
  dispatch: React.Dispatch<TripAction>;
}

const TripContext = createContext<TripContextValue | null>(null);

/**
 * The single-choice `style` predates vibes and is still what the trip cards and
 * presets read, so it keeps being written — taken from the first vibe rather
 * than asked for a second time. Nightlife and beach have no style of their own;
 * they land on the nearest one rather than inventing a sixth nothing renders.
 *
 * Same table as the mobile app's (apps/mobile/src/features/discover/state/
 * trips.ts) so a trip means the same thing on both.
 */
const STYLE_BY_VIBE: Record<Vibe, TripStyle> = {
  adventure: "adventure",
  desert: "adventure",
  "chill-spa": "relaxed",
  "beach-water": "relaxed",
  romantic: "relaxed",
  nightlife: "relaxed",
  foodie: "foodie",
  culture: "culture",
  family: "family",
};

export function styleFromVibes(vibes: readonly Vibe[] | undefined): TripStyle {
  const first = vibes?.[0];
  return first ? STYLE_BY_VIBE[first] : "culture";
}

/**
 * What a venue is called right now, written onto the stop as it is saved.
 *
 * The same three things the row shows, so a stop the catalogue cannot resolve
 * still reads as the place the traveller chose instead of disappearing out of
 * the day. It is a fallback, not a cache: a resolved item always wins, so a
 * renamed or re-photographed venue still updates everywhere.
 */
function snapshotOf(item: DiscoverItem): TripStopSnapshot {
  return {
    title: item.title,
    image: item.image,
    kind: item.subtitle || item.pinLabel || null,
  };
}

/**
 * Reorder a day's stops, leaving the times where they were.
 *
 * A day is a sequence of slots — 10:00, then 13:30, then 19:00 — and dragging a
 * place up the list means "do this one first", not "do this one at 19:00 but
 * print it at the top". If the times travelled with the stops, one drag would
 * leave the day listed out of chronological order, which is the one thing a
 * plan has to be. So the times belong to the positions: whatever ends up in
 * slot two gets slot two's time, and a slot with no time stays empty.
 *
 * This is why `reorderStops` below does not dispatch the reducer's
 * REORDER_STOPS, which moves the stops and their times together.
 */
export function reorderStopsKeepingTimes(
  stops: readonly TripStop[],
  stopIds: readonly string[],
): TripStop[] {
  const byId = new Map(stops.map((stop) => [stop.id, stop]));
  const next = stopIds
    .map((id) => byId.get(id))
    .filter((stop): stop is TripStop => stop !== undefined);

  // A partial or unknown id list would silently drop stops, which is worse
  // than not reordering at all.
  if (next.length !== stops.length) return [...stops];

  return next.map((stop, index) => {
    const slot = stops[index];
    if (!slot) return stop;
    if (stop.startTime === slot.startTime && stop.endTime === slot.endTime) {
      return stop;
    }
    return { ...stop, startTime: slot.startTime, endTime: slot.endTime };
  });
}

/** The whole trip with one day resequenced, ready for REPLACE_TRIP. */
export function reorderTripStops(
  trip: Trip,
  dayId: string,
  stopIds: readonly string[],
): Trip {
  return {
    ...trip,
    days: trip.days.map((day) =>
      day.id === dayId
        ? { ...day, stops: reorderStopsKeepingTimes(day.stops, stopIds) }
        : day,
    ),
  };
}

/**
 * Give older stops the snapshot they were saved without.
 *
 * Snapshots only started being written when this shipped, so every trip made
 * before it is one failed catalogue request away from reading as deleted. The
 * catalogue is loaded on this surface anyway — the first time it can name a
 * stop, write it down. Returns null when a trip gained nothing, so a visit that
 * changes nothing does not rewrite localStorage.
 */
function tripWithSnapshots(
  trip: Trip,
  lookup: (itemId: string) => DiscoverItem | null,
): Trip | null {
  let changed = false;
  const days = trip.days.map((day) => ({
    ...day,
    stops: day.stops.map((stop) => {
      if (stop.snapshot) return stop;
      const item = lookup(stop.itemId);
      if (!item) return stop;
      changed = true;
      return { ...stop, snapshot: snapshotOf(item) };
    }),
  }));
  return changed ? { ...trip, days } : null;
}

/**
 * The catalogue, when there is one to read.
 *
 * Plain `useContext` rather than `useDiscoverContext` on purpose: trips are
 * stored and read in trees that have no Discover provider (the share-link
 * hydrator, tests), and a stop with no catalogue to check against is a normal
 * state, not a crash.
 */
function useCatalogue() {
  const discover = useContext(DiscoverContext);
  const items = discover?.items;
  // A request that *failed* is neither pending nor loaded. Rows have to be able
  // to tell "not here yet" from "not here at all", or one bad connection makes
  // every trip read as deleted.
  const ready =
    discover !== null && !discover.isLoadingItems && discover.itemsError === null;
  const lookup = useMemo(() => {
    const byId = new Map((items ?? []).map((item) => [item.id, item]));
    return (itemId: string): DiscoverItem | null => byId.get(itemId) ?? null;
  }, [items]);
  return { lookup, ready };
}

export function TripProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(tripReducer, undefined, createInitialTripState);
  /** False until localStorage has been read. Nothing is written back before then. */
  const [hydrated, setHydrated] = useState(false);

  /**
   * Trips live in localStorage, which the server cannot read.
   *
   * Seeding the reducer from it — which is what this used to do — made the
   * first client render disagree with the server's, and React answers a
   * hydration mismatch by throwing the client tree away and keeping the
   * server's. So a traveller with saved trips could be shown "No trips yet"
   * and stay there, on their own trips page, with no way back but a reload.
   *
   * Start empty, exactly as the server does, then read storage in a layout
   * effect: it lands in the hydration commit, before the browser paints, so
   * the empty state never actually reaches the screen.
   */
  useIsomorphicLayoutEffect(() => {
    const stored = readTrips();
    if (stored.length > 0) {
      const storedActiveId = readActiveTripId();
      const activeTripId =
        storedActiveId && stored.some((t) => t.id === storedActiveId)
          ? storedActiveId
          : stored[0]?.id ?? null;
      dispatch({ type: "HYDRATE", trips: stored, activeTripId });
    }
    setHydrated(true);
  }, []);

  // Persist trips on change — but never before the read above, or the empty
  // first render would erase the trips it is about to load.
  const origin = useRef(generateId("trip-provider")).current;
  useEffect(() => {
    if (!hydrated) return;
    writeTrips(state.trips, origin);
  }, [hydrated, state.trips, origin]);

  // Someone else rewrote the stored trips: account sync bringing a trip from
  // another device, the AI chat adding one, or another tab. Read them back so
  // the planner shows what is stored. A copy equal to ours is ignored, and
  // writeTrips skips no-op writes, so the two sides never echo each other.
  const tripsRef = useRef(state.trips);
  tripsRef.current = state.trips;
  const activeRef = useRef(state.activeTripId);
  activeRef.current = state.activeTripId;
  useEffect(() => {
    if (!hydrated || typeof window === "undefined") return;
    const reload = () => {
      const stored = readTrips();
      if (JSON.stringify(stored) === JSON.stringify(tripsRef.current)) return;
      const active = activeRef.current;
      const activeTripId =
        active && stored.some((t) => t.id === active) ? active : stored[0]?.id ?? null;
      dispatch({ type: "HYDRATE", trips: stored, activeTripId });
    };
    const onChanged = (e: Event) => {
      const detail = (e as CustomEvent<TripsChangedDetail>).detail;
      if (detail?.origin === origin) return;
      reload();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === TRIPS_STORAGE_KEY) reload();
    };
    window.addEventListener(TRIPS_CHANGED_EVENT, onChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(TRIPS_CHANGED_EVENT, onChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [hydrated, origin]);

  // Persist the active trip id so it survives reloads / cross-page hops.
  useEffect(() => {
    if (!hydrated) return;
    writeActiveTripId(state.activeTripId);
  }, [hydrated, state.activeTripId]);

  // Trips saved before stops carried their own name and photo get them the
  // first time the catalogue can name them. Done here rather than in the
  // itinerary view because a stop is worth naming whichever surface is open,
  // and once written it never has to be fetched again.
  const { lookup, ready } = useCatalogue();
  useEffect(() => {
    if (!ready) return;
    for (const trip of state.trips) {
      const filled = tripWithSnapshots(trip, lookup);
      if (filled) dispatch({ type: "REPLACE_TRIP", trip: filled });
    }
  }, [ready, lookup, state.trips]);

  const value = useMemo<TripContextValue>(() => ({ state, dispatch }), [state]);

  return (
    <TripContext.Provider value={value}>{children}</TripContext.Provider>
  );
}

export function useTripContext(): TripContextValue {
  const ctx = useContext(TripContext);
  if (ctx === null) {
    throw new Error("useTripContext must be used inside <TripProvider>");
  }
  return ctx;
}

export interface CreateTripInput {
  name: string;
  dayCount: number;
  city: string;
  /** What the trip is for. Empty means no preference — nothing is suggested. */
  vibes?: readonly Vibe[];
  /** Day 1, YYYY-MM-DD. Left out, the trip starts the day it is made. */
  startDate?: string;
  /** The party, everyone. Left out, two. */
  people?: number;
  /** One age per child inside `people`. Left out, all adults. */
  childAges?: readonly number[];
  /**
   * The route, when the trip visits more than one city: each city and its
   * days, in order. When given, it sets the days and `city` (its first leg),
   * and `dayCount` is ignored.
   */
  route?: readonly RouteLeg[];
}

/** A party size the trip can store: whole, at least one, at most the cap. */
export function clampPeople(people: number | undefined): number {
  if (typeof people !== "number" || !Number.isFinite(people)) return DEFAULT_TRIP_PEOPLE;
  return Math.min(MAX_TRIP_PEOPLE, Math.max(1, Math.round(people)));
}

// Aggregator hook matching the spec's documented API — combines state + actions.
export function useTrip() {
  const { state } = useTripContext();
  const actions = useTripActions();
  return { state, actions };
}

export function useTripActions() {
  const { state, dispatch } = useTripContext();
  const { lookup } = useCatalogue();

  // The actions have to read the trip they are about to rewrite and the
  // catalogue they are naming a stop from, but taking either as a memo
  // dependency would hand every consumer a brand-new actions object on every
  // keystroke elsewhere in the trip. Refs keep the callbacks stable and the
  // data current.
  const stateRef = useRef(state);
  stateRef.current = state;
  const lookupRef = useRef(lookup);
  lookupRef.current = lookup;

  return useMemo(
    () => ({
      createTrip: (input: CreateTripInput): Trip => {
        const tripId = generateId("trip");
        const route = cleanRoute(input.route ?? []);
        const days: TripDay[] =
          route.length > 0
            ? daysFromRoute(route, () => generateId("day"))
            : Array.from({ length: input.dayCount }, (_, i) => ({
                id: generateId("day"),
                label: `Day ${i + 1}`,
                stops: [],
              }));
        const vibes = input.vibes ?? [];
        const trip: Trip = {
          id: tripId,
          name: input.name,
          city: route[0]?.city ?? input.city,
          // Still written, from the first vibe: the trip cards and presets read
          // `style`, and asking the same question twice would only let the two
          // answers disagree.
          style: styleFromVibes(vibes),
          createdAt: new Date().toISOString(),
          days,
          vibes,
          people: clampPeople(input.people),
          ...(input.childAges && input.childAges.length > 0 ? { childAges: input.childAges } : {}),
        };
        // Through the migration so a trip made without dates gets the same
        // start an old one would: today.
        const stored = migrateTrip(
          input.startDate ? { ...trip, startDate: input.startDate } : trip,
        );
        dispatch({ type: "CREATE_TRIP", trip: stored });
        return stored;
      },
      // Import a Trip object verbatim (used by share-link hydration and the
      // ready trips). Preserves the existing id so it idempotently round-trips.
      createTripRaw: (trip: Trip) =>
        dispatch({ type: "CREATE_TRIP", trip: migrateTrip(trip) }),
      /**
       * Change when day 1 is and who is coming, without touching the days.
       * `childAges` left out keeps the trip's children as they are.
       */
      setTripWhen: (tripId: string, startDate: string, people: number, childAges?: readonly number[]) => {
        const trip = stateRef.current.trips.find((tr) => tr.id === tripId);
        if (!trip) return;
        dispatch({
          type: "REPLACE_TRIP",
          trip: migrateTrip({
            ...trip,
            startDate,
            people: clampPeople(people),
            childAges: childAges ?? trip.childAges,
          }),
        });
      },
      replaceTrip: (trip: Trip) => dispatch({ type: "REPLACE_TRIP", trip }),
      deleteTrip: (tripId: string) =>
        dispatch({ type: "DELETE_TRIP", tripId }),
      setActiveTrip: (tripId: string | null) =>
        dispatch({ type: "SET_ACTIVE_TRIP", tripId }),
      renameTrip: (tripId: string, name: string) =>
        dispatch({ type: "RENAME_TRIP", tripId, name }),
      /** Move one day to another city; the rest of the route stays. */
      setDayCity: (tripId: string, dayId: string, city: string) =>
        dispatch({ type: "SET_DAY_CITY", tripId, dayId, city }),
      /** Sleep at `stay` on these nights (one city's), or nowhere with null. */
      setLegStay: (tripId: string, nightDayIds: readonly string[], stay: TripStayRef | null) =>
        dispatch({ type: "SET_LEG_STAY", tripId, nightDayIds, stay }),
      addDay: (tripId: string, label: string) =>
        dispatch({
          type: "ADD_DAY",
          tripId,
          day: { id: generateId("day"), label, stops: [] },
        }),
      removeDay: (tripId: string, dayId: string) =>
        dispatch({ type: "REMOVE_DAY", tripId, dayId }),
      addStop: (
        tripId: string,
        dayId: string,
        itemId: string,
        note?: string,
        source?: TripStopSource,
      ) => {
        // A stop stores an id, and an id on its own says nothing: the day it
        // was saved from is the last moment we are certain what the place is
        // called. Every caller here has the id and nothing else and the
        // catalogue is right there, so the name and photo are written once,
        // centrally, rather than threaded through four dialogs that would each
        // have to remember.
        const item = lookupRef.current(itemId);
        const stop: TripStop = {
          id: generateId("stop"),
          itemId,
          note,
          source,
          snapshot: item ? snapshotOf(item) : undefined,
        };
        dispatch({ type: "ADD_STOP", tripId, dayId, stop });
        return stop;
      },
      removeStop: (tripId: string, dayId: string, stopId: string) =>
        dispatch({ type: "REMOVE_STOP", tripId, dayId, stopId }),
      // Not the reducer's REORDER_STOPS, which moves the stops and their times
      // together: a day is a sequence of slots, so the times stay where they
      // are and the places move between them. See reorderStopsKeepingTimes.
      reorderStops: (
        tripId: string,
        dayId: string,
        stopIds: readonly string[]
      ) => {
        const trip = stateRef.current.trips.find((tr) => tr.id === tripId);
        if (!trip || !trip.days.some((d) => d.id === dayId)) return;
        dispatch({
          type: "REPLACE_TRIP",
          trip: reorderTripStops(trip, dayId, stopIds),
        });
      },
      moveStop: (
        tripId: string,
        fromDayId: string,
        toDayId: string,
        stopId: string,
      ) =>
        dispatch({
          type: "MOVE_STOP",
          tripId,
          fromDayId,
          toDayId,
          stopId,
        }),
      updateStopTime: (
        tripId: string,
        dayId: string,
        stopId: string,
        startTime: string | undefined,
        endTime: string | undefined,
      ) =>
        dispatch({
          type: "UPDATE_STOP_TIME",
          tripId,
          dayId,
          stopId,
          startTime,
          endTime,
        }),
      updateStopNote: (
        tripId: string,
        dayId: string,
        stopId: string,
        note: string | undefined,
      ) =>
        dispatch({
          type: "UPDATE_STOP_NOTE",
          tripId,
          dayId,
          stopId,
          note,
        }),
      sortDayByTime: (tripId: string, dayId: string) =>
        dispatch({ type: "SORT_DAY_BY_TIME", tripId, dayId }),
      setAirportTransfer: (tripId: string, enabled: boolean) =>
        dispatch({ type: "SET_AIRPORT_TRANSFER", tripId, enabled }),
      openOnboarding: () => dispatch({ type: "OPEN_ONBOARDING" }),
      advanceOnboarding: () => dispatch({ type: "ADVANCE_ONBOARDING" }),
      closeOnboarding: () => dispatch({ type: "CLOSE_ONBOARDING" }),
      openAddStop: (dayId: string) =>
        dispatch({ type: "OPEN_ADD_STOP", dayId }),
      closeAddStop: () => dispatch({ type: "CLOSE_ADD_STOP" }),
    }),
    [dispatch],
  );
}

export { TripContext };
